import React, { useState, useEffect } from 'react';
import './RaceSelector.css';
import { getApiUrl } from '../config';

interface Race {
  id: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  pilots?: number;
  status?: string;
}

interface RaceSelectorProps {
  onRaceSelect: (raceId: string) => void;
}

const RaceSelector: React.FC<RaceSelectorProps> = ({ onRaceSelect }) => {
  const [races, setRaces] = useState<Race[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch races from the API
        const response = await fetch(getApiUrl('/tracking/external/races'));

        if (!response.ok) {
          throw new Error(`Failed to fetch races: ${response.status}`);
        }

        const data = await response.json();
        console.log('Fetched races from API:', data);

        // Transform API data to our Race interface
        const transformedRaces: Race[] = data.map((race: any) => ({
          id: race._id,
          name: race.race_name,
          description: `Format: ${race.race_format === '0' ? 'Standard' : race.race_format === '4' ? 'Championship' : 'Custom'}`,
          location: race.location,
          startDate: race.date,
          endDate: race.end_date,
          pilots: race.pilots_count,
          status: race.status === 'Provisional Results' || race.status === 'Results' ? 'active' : race.status
        }));

        // Sort races by status (active first) and then by name
        transformedRaces.sort((a, b) => {
          if (a.status === 'active' && b.status !== 'active') return -1;
          if (a.status !== 'active' && b.status === 'active') return 1;
          return a.name.localeCompare(b.name);
        });

        setRaces(transformedRaces);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching races:', err);
        setError('Failed to fetch races. Please try again later.');
        setLoading(false);

        // Fallback to demo race
        setRaces([
          {
            id: '68aadbb85da525060edaaebf',
            name: 'HFSS App Testing',
            description: 'Test race for development',
            location: 'Laveno',
            startDate: '2025-01-01',
            endDate: '2026-12-01',
            pilots: 3,
            status: 'active'
          }
        ]);
      }
    };

    fetchRaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRaces = races.filter(race =>
    race.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    race.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    race.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRaceClick = (raceId: string) => {
    // Update URL with race parameter
    const url = new URL(window.location.href);
    url.searchParams.set('race', raceId);
    window.history.pushState({}, '', url);
    onRaceSelect(raceId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="race-selector-container">
      <div className="race-selector">
        <div className="race-selector-header">
          <img
            src="/hfss_logo.png"
            alt="HFSS Logo"
            className="race-selector-logo"
          />
          <h1>Select a Race</h1>
          <p>Choose a race to view live tracking data</p>
        </div>

        {!loading && races.length > 0 && (
          <div className="race-search">
            <input
              type="text"
              placeholder="Search races..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="race-search-input"
            />
          </div>
        )}

        {loading && (
          <div className="race-loading">
            <div className="spinner"></div>
            <p>Loading available races...</p>
          </div>
        )}

        {error && (
          <div className="race-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {!loading && !error && filteredRaces.length === 0 && searchTerm && (
          <div className="race-no-results">
            <p>No races found matching "{searchTerm}"</p>
          </div>
        )}

        {!loading && !error && races.length === 0 && (
          <div className="race-no-races">
            <p>No races available at the moment</p>
            <p className="race-hint">Check back later or contact the administrator</p>
          </div>
        )}

        {!loading && !error && filteredRaces.length > 0 && (
          <div className="race-grid">
            {filteredRaces.map((race) => (
              <div
                key={race.id}
                className={`race-card ${race.status === 'active' ? 'race-active' : ''}`}
                onClick={() => handleRaceClick(race.id)}
              >
                <div className="race-card-header">
                  <h3>{race.name}</h3>
                  {race.status === 'active' && (
                    <span className="race-badge race-badge-active">LIVE</span>
                  )}
                </div>
                {race.description && (
                  <p className="race-description">{race.description}</p>
                )}
                <div className="race-details">
                  {race.location && (
                    <div className="race-detail">
                      <span className="race-icon">📍</span>
                      <span>{race.location}</span>
                    </div>
                  )}
                  {(race.startDate || race.endDate) && (
                    <div className="race-detail">
                      <span className="race-icon">📅</span>
                      <span>
                        {race.startDate && race.endDate && race.startDate !== race.endDate
                          ? `${formatDate(race.startDate)} - ${formatDate(race.endDate)}`
                          : formatDate(race.startDate || race.endDate)}
                      </span>
                    </div>
                  )}
                  {race.pilots !== undefined && (
                    <div className="race-detail">
                      <span className="race-icon">🪂</span>
                      <span>{race.pilots} pilots</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="race-selector-footer">
          <p className="race-manual-entry">
            Have a race ID? Enter it directly in the URL:
            <code>?race=YOUR_RACE_ID</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RaceSelector;